import { ListItemIcon } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import useTheme from "@material-ui/core/styles/useTheme";
import SvgIcon from "@material-ui/core/SvgIcon";
import AssessmentIcon from "@material-ui/icons/AssessmentOutlined";
import LabelIcon from "@material-ui/icons/LabelOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import classNames from "classnames";
import * as React from "react";
import { MenuItemLink, usePermissions, useTranslate } from "react-admin";
import { useSelector } from "react-redux";

import { ReactComponent as AdminIcon } from "../assets/icons/sidebar/admin.svg";
import { ReactComponent as DashboardIcon } from "../assets/icons/sidebar/dashboard.svg";
import { ReactComponent as DashboardActiveIcon } from "../assets/icons/sidebar/dashboard_active.svg";
import { ReactComponent as DriverIcon } from "../assets/icons/sidebar/driver.svg";
import { ReactComponent as DriverActiveIcon } from "../assets/icons/sidebar/driver_active.svg";
import { ReactComponent as InvoiceIcon } from "../assets/icons/sidebar/invoice.svg";
import { ReactComponent as InvoiceActiveIcon } from "../assets/icons/sidebar/invoice_active.svg";
import { ReactComponent as PhotoIcon } from "../assets/icons/sidebar/photo_control.svg";
import { ReactComponent as PhotoActiveIcon } from "../assets/icons/sidebar/photo_control_active.svg";
import { ReactComponent as PlusIcon } from "../assets/icons/sidebar/plus.svg";
import { ReactComponent as SettingsIcon } from "../assets/icons/sidebar/settings.svg";
import { ReactComponent as SettingsActiveIcon } from "../assets/icons/sidebar/settings_active.svg";
import { ReactComponent as SupportIcon } from "../assets/icons/sidebar/support.svg";
import { ReactComponent as SupportActiveIcon } from "../assets/icons/sidebar/support_active.svg";
import { ReactComponent as OrganizationIcon } from "../assets/icons/tree.svg";
import PermissionUtils from "../common/permissionUtils";
import { useGlobalSettings } from "../hooks/global-settings";
import { usePendingPhotoControlsCount } from "../hooks/photo-controls";

const useMenuItemStyles = makeStyles(theme => ({
	root: {
		width: "100%",
		height: 58,
		borderLeft: "4px solid transparent",
		fontWeight: 600,
	},
	active: {
		color: theme.palette.gray.main,
	},
}));
const useNewMenuItemStyles = makeStyles(theme => ({
	root: {
		height: 58,
		width: "calc(100% - 4px)",
		fontWeight: "bold",
		color: theme.palette.text.secondary,
		marginLeft: 4,
	},
	icon: { minWidth: 36 },
}));

const useStyles = makeStyles(theme => ({
	container: {
		width: "100%",
	},
	active: {
		backgroundImage: `linear-gradient(to left, ${theme.palette.primary.light}, #ffffff)`,
		borderLeft: `4px solid ${theme.palette.primary.main}`,
		color: theme.palette.primary.main,
	},
	":hover": {
		display: "none",
	},
}));

const MyMenuItem = ({ primaryText, sidebarIsOpen, leftIcon, ...rest }) => {
	const menuItemStyles = useMenuItemStyles();
	return (
		<MenuItemLink
			classes={menuItemStyles}
			primaryText={sidebarIsOpen ? primaryText : ""}
			leftIcon={
				<SvgIcon component="span" style={{ width: "unset", height: "unset" }}>
					{leftIcon}
				</SvgIcon>
			}
			{...rest}
		/>
	);
};

const NewMenu = ({ onMenuClick }) => {
	const translate = useTranslate();
	const classes = useStyles();
	const permissions = new PermissionUtils(usePermissions());
	const open = useSelector(state => state.admin.ui.sidebarOpen);
	const activeRoute = useSelector(state => state.router.location.pathname);
	const theme = useTheme();
	const menuClasses = useNewMenuItemStyles();
	const { settings: globalSettings } = useGlobalSettings();
	const { pendingPhotoControlsCount } = usePendingPhotoControlsCount();

	return (
		<>
			<MyMenuItem
				key="dashboard"
				to="/dashboard"
				className={activeRoute === "/dashboard" ? classes.active : null}
				primaryText={translate("pages.dashboard.title")}
				sidebarIsOpen={open}
				leftIcon={
					activeRoute === "/dashboard" ? <DashboardActiveIcon /> : <DashboardIcon />
				}
				onClick={onMenuClick}
			/>
			<MyMenuItem
				key="drivers"
				to="/drivers"
				className={activeRoute === "/drivers" ? classes.active : null}
				primaryText={translate("pages.driverList.title")}
				leftIcon={activeRoute === "/drivers" ? <DriverActiveIcon /> : <DriverIcon />}
				sidebarIsOpen={open}
				onClick={onMenuClick}
			/>
			<MenuItem
				component="a"
				href="/driver/create"
				tabIndex={0}
				className={menuClasses.root}
				onClick={onMenuClick}
			>
				<ListItemIcon className={classNames(classes.icon, menuClasses.icon)}>
					<PlusIcon />
				</ListItemIcon>
				{open && translate("pages.driverCreate.title")}
			</MenuItem>
			{(permissions.hasPhotoControl() ||
				permissions.hasPhotoRollout() ||
				globalSettings?.allowPhotoControl) && (
				<MyMenuItem
					key="photo-controls"
					to="/photo-controls"
					className={activeRoute === "/photo-controls" ? classes.active : null}
					sidebarIsOpen={open}
					primaryText={
						<span style={{ display: "flex", alignItems: "center", gap: 6 }}>
							{translate("photoControls.plural")}
							{pendingPhotoControlsCount > 0 && (
								<span
									style={{
										backgroundColor: "red",
										color: "white",
										borderRadius: "50%",
										width: 20,
										height: 20,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										fontSize: 12,
									}}
								>
									{pendingPhotoControlsCount}
								</span>
							)}
						</span>
					}
					leftIcon={
						activeRoute === "/photo-controls" ? <PhotoActiveIcon /> : <PhotoIcon />
					}
					onClick={onMenuClick}
				/>
			)}
			{(permissions.isSuperAdminV2() || permissions.isFpm()) && (
				<MenuItem
					component="a"
					href={`/organization/${permissions.getSuperTenantId()}/managers`}
					tabIndex={0}
					className={menuClasses.root}
					onClick={onMenuClick}
				>
					<ListItemIcon className={classNames(classes.icon, menuClasses.icon)}>
						<AdminIcon />
					</ListItemIcon>
					{open && translate("pages.admin.title")}
				</MenuItem>
			)}

			<MyMenuItem
				key="orderLabel"
				to="/admin/order-label"
				className={activeRoute.includes("admin/order-label") ? classes.active : null}
				primaryText={translate("labels.label")}
				sidebarIsOpen={open}
				leftIcon={
					<LabelIcon
						style={{
							color: activeRoute.includes("admin/order-label")
								? theme.palette.primary.main
								: theme.palette.gray.main,
						}}
					/>
				}
				onClick={onMenuClick}
			/>
			<MyMenuItem
				key="driversReport"
				to="/drivers-report"
				className={activeRoute.includes("drivers-report") ? classes.active : null}
				primaryText={translate("driversReport.label")}
				sidebarIsOpen={open}
				leftIcon={
					<AssessmentIcon
						style={{
							color: activeRoute.includes("drivers-report")
								? theme.palette.primary.main
								: theme.palette.gray.main,
						}}
					/>
				}
				onClick={onMenuClick}
			/>
			{permissions.isSuperAdmin() && (
				<MyMenuItem
					key="invoice"
					to="/invoices"
					className={activeRoute.includes("invoices") ? classes.active : null}
					primaryText={translate("invoices.label")}
					sidebarIsOpen={open}
					leftIcon={
						activeRoute.includes("invoices") ? <InvoiceActiveIcon /> : <InvoiceIcon />
					}
					onClick={onMenuClick}
				/>
			)}

			<MyMenuItem
				key="support"
				to="/support"
				className={activeRoute.includes("support") ? classes.active : null}
				primaryText={translate("pages.support.title")}
				leftIcon={activeRoute.includes("support") ? <SupportActiveIcon /> : <SupportIcon />}
				sidebarIsOpen={open}
				onClick={onMenuClick}
			/>

			<MyMenuItem
				key="settings"
				to="/settings"
				className={activeRoute.includes("settings") ? classes.active : null}
				primaryText={translate("pages.settings.title")}
				sidebarIsOpen={open}
				leftIcon={
					activeRoute.includes("settings") ? <SettingsActiveIcon /> : <SettingsIcon />
				}
				onClick={onMenuClick}
			/>
			{permissions.isSuperAdmin() && (
				<>
					<MenuItem
						component="a"
						href="/organizations"
						tabIndex={0}
						className={menuClasses.root}
						onClick={onMenuClick}
					>
						<ListItemIcon className={classNames(classes.icon, menuClasses.icon)}>
							<OrganizationIcon />
						</ListItemIcon>
						{open && translate("pages.organizations.title")}
					</MenuItem>
					<MenuItem
						component="a"
						href="/templates"
						tabIndex={0}
						className={menuClasses.root}
						onClick={onMenuClick}
					>
						<ListItemIcon className={classNames(classes.icon, menuClasses.icon)}>
							<MailOutlineIcon color="disabled" />
						</ListItemIcon>
						{open && translate("pages.templates.title")}
					</MenuItem>
				</>
			)}
		</>
	);
};

const Menu = ({ onMenuClick }) => <NewMenu onMenuClick={onMenuClick} />;

export default Menu;
